import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { AngularPlugin, IErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights, ICustomProperties, IDependencyTelemetry, IEventTelemetry, IExceptionTelemetry, IMetricTelemetry, IPageViewTelemetry, ITraceTelemetry, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { CookieService } from 'ngx-cookie-service';
import { take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PosthogService } from './posthog.service';
import { DeploymentMetadata, VersionCheckService } from './version-check.service';

// https://github.com/microsoft/applicationinsights-angularplugin-js/blob/main/sample/applicationinsights-angularplugin-sample/src/app/telemetry.service.ts
@Injectable({ providedIn: 'root' })
export class ApplicationInsightsService {
    private readonly _destroyRef = inject(DestroyRef);
    private _router = inject(Router);
    private _authService = inject(AuthService);
    private _cookieService = inject(CookieService);
    private _posthogService = inject(PosthogService);
    private _versionCheckService = inject(VersionCheckService);

    private angularPlugin = new AngularPlugin();
    private appInsights = new ApplicationInsights({
        config: {
            ...this.getTelemetryConfig(environment.appInsightsKey),
            enableCorsCorrelation: true,
            enableRequestHeaderTracking: true,
            enableResponseHeaderTracking: true,
            correlationHeaderExcludedDomains: ['auth-store.statusnow.com', '*.auth0.com', 'stores.api.statusnow.com', '*.stores.api.statusnow.com'],
            extensions: [this.angularPlugin],
            extensionConfig: {
                [this.angularPlugin.identifier]: {
                    router: this._router,
                    errorServices: [new CustomErrorHandler()]
                }
            }
        }
    });

    authenticated = false;
    private currentUsername?: string;
    private deploymentMetadata?: DeploymentMetadata;
    private readonly _telemetryCtorPerf = performance.now();

    constructor() {
        this.addTelemetryInitializer();
        this.appInsights.loadAppInsights();
        this.trackTrace({ message: 'telemetrySdkReady' });

        this._versionCheckService.deploymentMetadata$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((metadata) => {
            this.deploymentMetadata = metadata;
            this.appInsights.context.application.ver = metadata.release;

            if (metadata.commitSha) {
                this.appInsights.context.application.build = metadata.commitSha;
            }
        });

        this._authService.isAuthenticated$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((isAuthenticated) => {
            this.authenticated = isAuthenticated;

            if (!isAuthenticated) {
                this.currentUsername = undefined;
                this.appInsights.clearAuthenticatedUserContext();
                return;
            }

            this._authService.user$.pipe(take(1), takeUntilDestroyed(this._destroyRef)).subscribe((user) => {
                if (!user) {
                    return;
                }

                const msToUser = Math.round(performance.now() - this._telemetryCtorPerf);
                this.trackMetric({ name: 'TimeToUserContextMs', average: msToUser });

                if (user.username) {
                    this.currentUsername = user.username;
                    this.appInsights.setAuthenticatedUserContext(user.username, undefined, true);
                }

                this.trackEvent(
                    { name: 'authAuthenticated' },
                    {
                        route: this._router.url || '/'
                    }
                );
                this._posthogService.capture('authAuthenticated', {
                    route: this._router.url || '/'
                });
            });
        });
    }

    // expose tracking methods
    trackEvent(event: IEventTelemetry, customProperties?: ICustomProperties) {
        this.appInsights.trackEvent(event, customProperties);
    }

    startTrackEvent(name?: string) {
        this.appInsights.startTrackEvent(name);
    }

    stopTrackEvent(name: string, properties?: Record<string, string>, measurements?: Record<string, number>): void {
        this.appInsights.stopTrackEvent(name, properties, measurements);
    }

    trackPageView(pageView?: IPageViewTelemetry) {
        this.appInsights.trackPageView(pageView);
    }

    startTrackPage(name?: string) {
        this.appInsights.startTrackPage(name);
    }

    stopTrackPage(name?: string, url?: string, properties?: Record<string, string>, measurements?: Record<string, number>) {
        this.appInsights.stopTrackPage(name, url, properties, measurements);
    }

    trackMetric(metric: IMetricTelemetry, properties?: ICustomProperties) {
        this.appInsights.trackMetric(metric, properties);
    }

    trackException(exception: IExceptionTelemetry, properties?: ICustomProperties) {
        this.appInsights.trackException(exception, properties);

        if (exception.exception) {
            this._posthogService.captureException(exception.exception, {
                source: 'applicationInsights.trackException',
                ...(properties as Record<string, string | number | boolean> | undefined)
            });
        }
    }

    trackTrace(message: ITraceTelemetry, properties?: ICustomProperties) {
        this.appInsights.trackTrace(message, properties);
    }

    trackDependencyData(dependency: IDependencyTelemetry) {
        this.appInsights.trackDependencyData(dependency);
    }

    flush() {
        this.appInsights.flush();
    }

    private getTelemetryConfig(connectionStringOrKey: string): { connectionString: string } | { instrumentationKey: string } {
        if (connectionStringOrKey.includes('InstrumentationKey=')) {
            return { connectionString: connectionStringOrKey };
        }

        return { instrumentationKey: connectionStringOrKey };
    }

    private addTelemetryInitializer(): void {
        this.appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
            item.data = item.data || {};

            if (this.currentUsername) {
                item.data['username'] = this.currentUsername;
            }

            const storeNumber = this.getStoreNumber();
            if (storeNumber) {
                item.data['storeNumber'] = storeNumber;

                const customer = this.getCustomer(storeNumber);
                if (customer) {
                    item.data['customer'] = customer;
                }
            }

            item.data['deviceCategory'] = this.getDeviceCategory();

            if (this.deploymentMetadata) {
                item.data['release'] = this.deploymentMetadata.release;

                if (this.deploymentMetadata.commitSha) {
                    item.data['commitSha'] = this.deploymentMetadata.commitSha;
                }
            }

            return true;
        });
    }

    private getStoreNumber(): string | undefined {
        const storeNumber = this._cookieService.get('storeNumber')?.trim();
        return storeNumber || undefined;
    }

    private getCustomer(storeNumber: string): string | undefined {
        const separatorIndex = storeNumber.indexOf('-');
        if (separatorIndex <= 0) {
            return undefined;
        }

        return storeNumber.slice(0, separatorIndex);
    }

    private getDeviceCategory(): 'mobile' | 'desktop' {
        return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent) ? 'mobile' : 'desktop';
    }
}

export class CustomErrorHandler implements IErrorService {
    handleError(error: unknown) {
        if (error && !environment.production) {
            console.error(error);
        }
    }
}
